import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import Column from '../../../../components/Column';
import SvgFragment from '../../../../fragments/SvgFragment';
import { useCampaignsRequest } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { useAppActions } from '../../../../stores/application/useAppActions';
import StyledModal from '../../../../components/modal/styledModals/StyledModal';
import SendingAccountsSection from '../../sections/SendingAccountsSection';
import CampaignSenderPicker from './CampaignSenderPicker';
import { ISenderIdentity } from '../../../../interfaces/models/campaign';
import { SENDERS_CHANGED_EVENT } from '../../utils/senderEvents';

/**
 * Sender identities — one card per person, showing both halves of the pair.
 *
 * The two halves are reported independently on purpose. A mailbox whose token has
 * expired shows a reconnect prompt, while the LinkedIn half keeps saying "ready",
 * because LinkedIn steps genuinely do keep running. Showing the whole sender as
 * broken would send people looking for a problem that isn't there.
 *
 * The mailbox is optional here. It only becomes mandatory when the sequence contains
 * an email step, which is checked at activation — asking every LinkedIn-only campaign
 * for a Gmail consent screen would be friction for nothing.
 */


/** How often, and for how long, to check whether an OAuth mailbox has landed. */
const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS  = 3 * 60 * 1000;

/**
 * How long to keep checking after the user closes the consent window. The provider's
 * notify webhook can still arrive a moment later, so closing the window is not proof
 * the connection failed.
 */
const POPUP_CLOSED_GRACE_MS = 12 * 1000;

/** Consent window size — tall and narrow, the shape every provider's sign-in expects. */
const POPUP_W = 520;
const POPUP_H = 700;

interface IProps {
  /**
   * Scopes the list to the senders this campaign actually sends as. They are derived
   * from the campaign's own LinkedIn accounts (proxy) or its creator (extension) —
   * there is nothing to pick, so there is no selection here.
   */
  campaignId?: string;
  /** True when the sequence has an email step — drives the "mailbox required" copy. */
  requiresEmail?: boolean;
  /**
   * Extension campaigns have no Unipile LinkedIn account to connect — the user's own
   * browser session IS the LinkedIn half. They become a sender by registering their
   * own LinkedIn profile, then attaching a mailbox to it.
   */
  executionMethod?: 'extension' | 'proxy';
  /**
   * Reports the loaded senders up so the wizard can block progress when the
   * sequence emails people but no selected sender has a working mailbox.
   */
  onIdentitiesLoaded?: (identities: ISenderIdentity[]) => void;
  /**
   * Same-site path the OAuth flow should return to. Without it the provider drops
   * the user on the campaigns list and they lose their place mid-setup.
   */
  returnTo?: string;
  /**
   * Mark the sender field as invalid — set once the wizard has refused to continue
   * because no sender is picked, or the picked one cannot send email.
   */
  showErrors?: boolean;
}

const CampaignSenderIdentities: FunctionComponent<IProps> = ({
  campaignId, requiresEmail = false, executionMethod = 'proxy',
  onIdentitiesLoaded, returnTo, showErrors = false,
}) => {
  const {
    getSenderIdentities, createMailConnectLink, setCampaignIdentities,
    createExtensionIdentity,
  } = useCampaignsRequest();
  const { setToast } = useAppActions();

  const [identities, setIdentities] = useState<ISenderIdentity[]>([]);
  const [loading, setLoading]       = useState(true);
  const [busyId, setBusyId]         = useState<string | null>(null);
  const [addingSelf, setAddingSelf] = useState(false);
  const [selfError, setSelfError]   = useState('');


  // Waiting on an OAuth round-trip. The consent screen opens in another tab, so this
  // one has to find out on its own whether the mailbox arrived — the proxy connect
  // flow polls for the same reason.
  const [waitingFor, setWaitingFor] = useState<string | null>(null);
  /** Settings, opened over the campaign rather than navigated to. */
  const [manageOpen, setManageOpen] = useState(false);
  /** What the picker shows while a save is in flight — see saveSelection. */
  const [pendingSelection, setPendingSelection] = useState<string[] | null>(null);
  /** Everything the picker can offer — the whole workspace, not just this campaign. */
  const [workspaceSenders, setWorkspaceSenders] = useState<ISenderIdentity[]>([]);
  /** The browser refused to open the consent window — nothing else reports this. */
  const [popupBlocked, setPopupBlocked] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** The consent window, so it can be closed for the user once the mailbox lands. */
  const popupRef = useRef<Window | null>(null);

  const closePopup = useCallback(() => {
    try { popupRef.current?.close(); } catch { /* already gone, or cross-origin */ }
    popupRef.current = null;
  }, []);

  /**
   * Stops watching. Deliberately does NOT touch the consent window: watchForMailbox
   * calls this first to cancel any previous watch, and closing here killed the window
   * that had just been opened one line earlier. Closing is an explicit act — on
   * success, on cancel, and on unmount.
   */
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setWaitingFor(null);
  }, []);

  /** The user gave up: stop watching and take the window away with it. */
  const cancelConnect = useCallback(() => {
    stopPolling();
    closePopup();
  }, [stopPolling, closePopup]);

  // Never leave an interval — or an orphaned consent window — behind.
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    try { popupRef.current?.close(); } catch { /* already gone */ }
  }, []);




  const isExtension = executionMethod === 'extension';

  /*
   * `loading` is the FIRST load only — it blanks the section, and a refresh must not.
   *
   * Every reload went through it, so closing the Settings dialog replaced the whole
   * senders block with "Loading senders…" for a second or two and then brought it back.
   * Nothing had changed on screen; it just looked like the section had fallen over.
   * A refresh now updates the list in place, under whatever is already drawn.
   */
  const loadedOnce = useRef(false);

  const load = useCallback(async () => {
    if (!loadedOnce.current) setLoading(true);
    const res: any = await getSenderIdentities(true, campaignId);
    const list = res?.data?.identities ?? res?.identities;
    /*
      Only replace the lists when a response actually carried one.

      A request that was cancelled — the hook drops any earlier call sharing its slug —
      resolves to nothing, and `?? []` turned that into "this campaign has no senders"
      and "the workspace has none to offer": an account picker with no rows in it, which
      is why a sender unticked by accident could not be ticked back on.
    */
    if (Array.isArray(list)) {
      setIdentities(list);
      onIdentitiesLoaded?.(list);
    }
    loadedOnce.current = true;
    setLoading(false);

    /*
      The picker offers every sender in the workspace, so it needs the unscoped list —
      the campaign's own is a subset and could never offer anyone new. Asked for live,
      so the LinkedIn health each row reports is current rather than cached.
    */
    const wres: any = await getSenderIdentities(true);
    const wlist = wres?.data?.identities ?? wres?.identities;
    if (Array.isArray(wlist)) setWorkspaceSenders(wlist);
    /*
      Keyed on campaignId, and that matters.

      With an empty dependency list this closed over whatever campaignId was on the
      FIRST render — and on a new campaign that is often nothing: the panel appears the
      moment an email step is added, while the draft is still being created. It then
      asked for the workspace list for the rest of the session, so the campaign-scoped
      read that DERIVES a sender from the campaign's LinkedIn account never ran, and the
      proxy chosen in step one never carried through to the email step.

      getSenderIdentities itself is rebuilt every render by the request hook, so it
      stays out of the list — depending on it would re-fire this forever.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  // Settings changed a sender — possibly after its dialog closed. See senderEvents.
  useEffect(() => {
    const onChanged = () => { void load(); };
    window.addEventListener(SENDERS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(SENDERS_CHANGED_EVENT, onChanged);
  }, [load]);

  /*
    Re-read the workspace list whenever the picker is opened.

    The rows it shows were fetched when this panel mounted; anything done since in
    another tab — a mailbox paired in Settings there — never reached it, so the picker
    offered "Connect mailbox" for a mailbox that was already connected. Its own scope,
    so it cannot cancel, or be cancelled by, load()'s fetch of the same list.
  */
  const refreshWorkspaceSenders = useCallback(async () => {
    const res: any = await getSenderIdentities(true, undefined, 'picker');
    const list = res?.data?.identities ?? res?.identities;
    if (Array.isArray(list)) setWorkspaceSenders(list);
    // getSenderIdentities is rebuilt every render by the request hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Register the current user as a sender using their own LinkedIn profile.
   * This is the only way in for an extension campaign — there is no Unipile LinkedIn
   * account to connect, so without this the screen is a dead end.
   */
  const addSelfAsSender = async () => {
    setAddingSelf(true);
    setSelfError('');
    // `all: true` on this request, so the envelope is intact: { success, data, message }
    const res: any = await createExtensionIdentity();
    const identity = res?.data?.identity;

    if (!identity) {
      // Every rejection this endpoint can produce — including auth and access
      // middleware — comes back as JSON carrying a message. So an undefined body
      // means no response arrived at all, and blaming the LinkedIn profile for that
      // sends people off re-opening LinkedIn to fix something else entirely.
      setSelfError(
        res?.message
        || res?.data?.message
        || (res === undefined
          ? 'The server did not respond to that request. Check your connection and try again — '
            + 'if it keeps failing, the API is unreachable and this is not a LinkedIn problem.'
          : 'Could not add you as a sender. Please try again.'),
      );
    }
    setAddingSelf(false);
    if (identity) load();
  };


  /**
   * Gmail/Outlook need a consent screen — Unipile's hosted flow handles it.
   *
   * The flow reports back to a notify URL built from APP_URL, which the provider
   * cannot reach on a local or otherwise private host: the mailbox is created on
   * their side but never paired here, and the campaign just keeps saying "No mailbox".
   * So rather than leaving the user staring at that, the picker is opened straight
   * away — the new mailbox shows up there within a few seconds of finishing consent
   * and can be paired directly, no callback involved.
   */
  const connectNewMailbox = async (identityId: string) => {
    setPopupBlocked(false);

    // Opened BEFORE the request, while the click is still on the stack.
    //
    // A sized popup rather than a tab, the way every OAuth consent screen behaves:
    // the campaign stays visible behind it, and we can close it ourselves the moment
    // the mailbox lands. But a browser only grants window.open during a user gesture,
    // and awaiting the link request spends that gesture — so open a blank window now
    // and point it at the URL when it arrives. Opening after the await works in Chrome
    // and is silently blocked in Safari.
    //
    // Deliberately without `noopener`: that returns null instead of a handle, and the
    // handle is the whole point. The window only ever loads Unipile's hosted flow.
    const popup = openConsentPopup();
    popupRef.current = popup;

    // Blocked popups are silent: no window, no error. Say so, rather than leaving
    // someone waiting on a consent screen that never appeared.
    if (!popup) { setPopupBlocked(true); return; }

    setBusyId(identityId);
    const res: any = await createMailConnectLink(identityId, returnTo);
    const url = res?.data?.url ?? res?.url;
    setBusyId(null);

    // Never strand an empty window when the link could not be made.
    if (!url) { closePopup(); return; }

    popup.location.replace(url);
    popup.focus?.();
    watchForMailbox(identityId);
  };

  /**
   * Poll until the mailbox shows up on this identity, so the screen the user started
   * from confirms the connection itself — rather than leaving them to guess whether
   * it worked, or to hunt for it in the reuse list.
   *
   * Covers both routes: the notify webhook pairing it server-side, and the user
   * pairing it by hand from the picker in the meantime.
   */
  const watchForMailbox = useCallback((identityId: string) => {
    stopPolling();
    setWaitingFor(identityId);

    const startedAt = Date.now();
    // When the user closes the consent window themselves, the mailbox may still be on
    // its way in via the notify webhook — so don't stop dead, but don't keep the
    // "waiting" message up for the full timeout either.
    let popupClosedAt: number | null = null;

    pollRef.current = setInterval(async () => {
      // Give up after a few minutes rather than polling forever on a window someone
      // walked away from. The picker below still works after that.
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) { stopPolling(); return; }

      if (popupRef.current?.closed) {
        if (popupClosedAt === null) popupClosedAt = Date.now();
        if (Date.now() - popupClosedAt > POPUP_CLOSED_GRACE_MS) { stopPolling(); return; }
      }

      /*
        The WORKSPACE list, and it updates the workspace list only.

        A mailbox can be connected for any sender the picker offers, including one this
        campaign has not selected — so the campaign-scoped list would never see it
        arrive. What this must not do is what it used to: write the workspace list into
        `identities`, the campaign's own selection. That silently made every sender in
        the workspace look selected, and handed the same wrong list to the campaign page
        through onIdentitiesLoaded.
      */
      const res: any = await getSenderIdentities(true, undefined, 'mailbox-poll');
      const list = res?.data?.identities ?? res?.identities;
      if (!Array.isArray(list)) return;
      setWorkspaceSenders(list);

      const mine = list.find((i: ISenderIdentity) => i._id === identityId);
      if (mine?.mail?.connected) {
        stopPolling();
        // The whole point of holding the handle: take the finished consent window
        // away instead of leaving it for the user to tidy up.
        closePopup();
        setToast({
          type: 'success',
          message: `${mine.mail.address || 'Mailbox'} is now paired with ${mine.name || 'this sender'}.`,
        });
        // The campaign's own list carries the mailbox too — reload it so the sender
        // stops reporting "no mailbox" on this screen and at validation.
        await load();
      }
    }, POLL_INTERVAL_MS);
    // getSenderIdentities is rebuilt each render by the request hook, and draftFrom
    // only reads the identity handed to it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopPolling, closePopup, load]);

  /**
   * Landing back here after consent, in whichever window the provider redirected.
   *
   * In the popup that is us running a second time inside a 520px window: there is
   * nothing for the user to do there, and the page that opened it is already polling,
   * so close it rather than render the whole app in a sliver. The opener would close
   * it a few seconds later anyway — this just avoids the flash.
   *
   * In a normal tab (popup blocked, or the user re-opened the link) there is no
   * opener, so fall through to watching, which is what that tab is for.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mailbox') !== 'connected') return;

    if (window.opener && window.opener !== window) { window.close(); return; }

    const pending = identities.find(i => !i.mail.connected);
    if (pending) watchForMailbox(pending._id);
    // Only on first load with the flag present.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);


  /*
    Pins the selection on the campaign. Reloads afterwards rather than trusting local
    state: the server also re-syncs the LinkedIn account pool and reassigns leads, so
    what comes back can differ from what was clicked.
  */
  const saveSelection = async (ids: string[]) => {
    if (!campaignId) return;

    /*
      Refused rather than saved.

      Persisting an empty list also pins the campaign, and a pinned empty list is not
      "fall back to the LinkedIn accounts" — it is "this campaign sends as nobody", for
      good. Unticking the last sender is far more likely to be a misclick than a
      decision, so say what is needed instead of writing a state that has to be undone.
    */
    if (!ids.length) {
      setToast({
        type: 'error',
        message: 'A campaign needs at least one sender. Pick who it should send as, or swap this one for another.',
      });
      return;
    }

    /*
      Tick the box now, reconcile after.

      The checkboxes read from `identities`, which only changes once the save and the
      reload have both completed — so a click appeared to do nothing for a second and
      then, if anything went wrong, silently undid itself. The pending set is what the
      picker renders until the server's answer replaces it.
    */
    setPendingSelection(ids);
    const res: any = await setCampaignIdentities(campaignId, ids);

    if (res?.data?.updated) {
      await load();
      setPendingSelection(null);
      return;
    }
    // Put the boxes back the way they were, and say why.
    setPendingSelection(null);
    setToast({ type: 'error', message: res?.message || 'Could not change the senders.' });
  };



  if (loading) return <Muted>Loading senders…</Muted>;

  /*
    "No senders yet" means the WORKSPACE has none — not that this campaign has picked
    none.

    This used to return on `identities.length === 0` alone, which short-circuited the
    whole panel before the picker rendered. Connect a LinkedIn account in the previous
    step, come back, and the campaign had nothing selected yet: a dead end telling you
    to connect an account you had just connected, with nothing on screen to select it
    with. With accounts available, the picker IS the empty state.
  */
  if (!identities.length && !workspaceSenders.length) {
    return (
      <EmptyBox>
        <EmptyTitle>No senders yet</EmptyTitle>
        <EmptyText>
          {isExtension
            ? 'This campaign sends LinkedIn steps from your own browser, so you are the sender. '
              + 'Add yourself here, then connect the mailbox your emails should come from.'
            : 'A sender is one person — their LinkedIn account and their mailbox, paired. Connect a '
              + 'LinkedIn account first; every lead is then assigned one sender and hears from that '
              + 'same person on both channels.'}
        </EmptyText>
        {isExtension && (
          <>
            <EmptyAction type="button" disabled={addingSelf} onClick={addSelfAsSender}>
              {addingSelf ? 'Adding…' : 'Add me as a sender'}
            </EmptyAction>
            {selfError && <ErrorText>{selfError}</ErrorText>}
          </>
        )}
      </EmptyBox>
    );
  }


  return (
    <SendersPanel>
      {/* The header names the thing; the actions belong to the senders themselves.
          A panel-level button could not say which sender it was for. */}
      <PanelHead>
        <Column gap="2px">
          <PanelTitle>Senders</PanelTitle>
          <PanelSub>Each lead is assigned one sender.</PanelSub>
        </Column>
        {/* Beside the title, not down among the rows: it opens settings for the whole
            workspace, so tying it to any one sender's row misread its scope. */}
        <ManageBtn type="button" onClick={() => setManageOpen(true)}>
          Manage accounts
        </ManageBtn>
      </PanelHead>

      {/*
        Which senders this campaign uses. Only once the campaign exists — before that
        there is nothing to pin the selection to, and offering the choice would lose it
        on the first save.
      */}
      {campaignId && (
        <PickerField>
          <PickerLabel>Connected account</PickerLabel>
          <CampaignSenderPicker
            options={workspaceSenders}
            selectedIds={pendingSelection ?? identities.map(i => i._id)}
            onChange={saveSelection}
            onOpen={() => { void refreshWorkspaceSenders(); }}
            onConnectMailbox={i => connectNewMailbox(i._id)}
            disabled={!!busyId}
            /* Red while a sender is missing, or has no mailbox for an email step —
               the two reasons the wizard refuses this step. */
            invalid={showErrors && (!identities.length || (requiresEmail && !identities.some(i => i.emailReady)))}
          />
          {/*
            No warning line under the picker.

            It said "Pick who this campaign sends as." — which is both awkward English
            and redundant: the field directly above already reads "No sender selected",
            and the wizard refuses to continue with none, naming the problem then. A
            standing orange line under an untouched field is a telling-off for not
            having done something yet.
          */}
        </PickerField>
      )}

      {/*
        The per-sender cards are gone: the picker above reports both halves for every
        account, so the cards restated it directly underneath.

        These two alerts stay, lifted out of that loop. They are the only feedback the
        mailbox connect flow has, and neither belongs to a particular sender — a
        blocked popup is a browser fact, and only one sign-in runs at a time.
      */}
      {popupBlocked && (
        <Alert $tone="danger">
          <strong>Your browser blocked the sign-in window.</strong>{' '}
          Allow pop-ups for this site, then try again.
          <AlertBtn type="button" onClick={() => setPopupBlocked(false)}>Dismiss</AlertBtn>
        </Alert>
      )}

      {waitingFor && (
        <Alert $tone="warn">
          <strong>Waiting for the sign-in to finish…</strong>{' '}
          Finish signing in in the window that opened — it closes on its own once the
          mailbox is connected.
          <AlertBtn type="button" onClick={cancelConnect}>Cancel</AlertBtn>
        </Alert>
      )}

      {/*
        Settings, over the campaign rather than instead of it. Navigating away mid-setup
        loses the wizard's place, and everything in here — the daily cap, the warm-up
        schedule, the signature, removing a mailbox — is a two-minute detour, not a
        change of task. Closing refetches, so whatever was changed shows up on the cards
        behind it.
      */}
      <StyledModal
        show={manageOpen}
        onHide={() => { setManageOpen(false); load(); }}
        width="min(1040px, 94vw)"
        backdrop
      >
        <ManageHead>
          <ManageTitle>Sending accounts</ManageTitle>
          <ManageClose type="button" onClick={() => { setManageOpen(false); load(); }} aria-label="Close">
            <SvgFragment type="close" />
          </ManageClose>
        </ManageHead>
        <ManageBody>
          <SendingAccountsSection inDialog />
        </ManageBody>
      </StyledModal>
    </SendersPanel>
  );
};

export default CampaignSenderIdentities;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Open a centred popup for the provider's consent screen.
 *
 * Deliberately takes no URL: it must be called synchronously from the click, before
 * the link request, so the caller navigates it afterwards. See connectNewMailbox.
 *
 * Centred on the browser window rather than the screen, so on a multi-monitor setup it
 * appears over the app instead of on whichever display the OS calls primary.
 *
 * Returns null when the browser blocked it — callers must handle that, because a
 * blocked popup throws nothing and logs nothing.
 */
function openConsentPopup(): Window | null {
  const baseLeft = window.screenLeft ?? window.screenX ?? 0;
  const baseTop  = window.screenTop ?? window.screenY ?? 0;
  const viewW = window.outerWidth || window.innerWidth || window.screen.width;
  const viewH = window.outerHeight || window.innerHeight || window.screen.height;

  const left = Math.max(0, Math.round(baseLeft + (viewW - POPUP_W) / 2));
  const top  = Math.max(0, Math.round(baseTop + (viewH - POPUP_H) / 2));

  // about:blank so the window exists immediately; the caller navigates it once the
  // provider link comes back.
  return window.open(
    'about:blank',
    'ld-mailbox-connect',
    `width=${POPUP_W},height=${POPUP_H},left=${left},top=${top},resizable=yes,scrollbars=yes`,
  );
}


/** A mailbox is still ramping for its first couple of weeks. */
// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;

/*
  One panel around the whole list, headed by the single action that starts a
  connection. The senders themselves are borderless rows inside it — they are facts
  about the campaign, not things to pick between, so boxing each one gave the section
  more visual weight than it earns.
*/
const ManageHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 16px 20px 12px; border-bottom: 1px solid #eef1f6;
`;

const ManageTitle = styled.div`
  font-family: NunitoSans; font-size: 16px; font-weight: 800; color: #0f172a;
`;

const ManageClose = styled.button`
  background: none; border: none; padding: 2px; cursor: pointer; line-height: 0;
  .svg { width: 18px; height: 18px; }
`;

/* The settings page brings its own padding; cap the height so a long account list
   scrolls inside the dialog instead of pushing it off screen. */
const ManageBody = styled.div`
  max-height: 72vh; overflow-y: auto;
`;







const SendersPanel = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fff;
  display: flex; flex-direction: column; gap: 12px;
`;

const ManageBtn = styled.button`
  flex-shrink: 0;
  border: 1.5px solid #e2e8f0; background: #fff; border-radius: 8px;
  padding: 7px 13px; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: #475569;
  &:hover { border-color: ${ACCENT}; color: ${ACCENT}; }
`;

const PickerField = styled.div`
  display: flex; flex-direction: column; gap: 5px;
  margin-bottom: 4px;
`;

const PickerLabel = styled.span`
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: #475569;
`;

const PanelHead = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
`;

const PanelTitle = styled.div`
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: #1e293b;
`;

const PanelSub = styled.div`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8;
`;





const Muted = styled.div`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8; line-height: 1.5;
`;

const ALERT_TONES = {
  danger: { color: '#991b1b', bg: '#fef2f2', border: '#fecaca' },
  warn:   { color: '#92400e', bg: '#fffbeb', border: '#fde68a' },
  ok:     { color: '#166534', bg: '#f0fdf4', border: '#bbf7d0' },
};

const Alert = styled.div<{ $tone: 'warn' | 'danger' | 'ok' }>`
  margin-top: 10px; padding: 10px 12px; border-radius: 8px;
  font-family: NunitoSans; font-size: 12px; line-height: 1.5;
  color: ${p => ALERT_TONES[p.$tone].color};
  background: ${p => ALERT_TONES[p.$tone].bg};
  border: 1px solid ${p => ALERT_TONES[p.$tone].border};
`;

const AlertBtn = styled.button`
  display: block; margin-top: 8px;
  border: 1px solid currentColor; background: none; color: inherit;
  border-radius: 6px; padding: 4px 10px; cursor: pointer;
  font-family: NunitoSans; font-size: 11px; font-weight: 700;
`;











/*
  Children stretch rather than shrink to their content. With flex-start, anything
  without an explicit width — an editor, a preview, an alert — collapsed to the width
  of the text inside it. The buttons that need to stay small sit inside a Row.
*/




const EmptyBox = styled.div`
  border: 1px dashed #cbd5e1; border-radius: 12px; padding: 16px; background: #f8fafc;
`;

const EmptyTitle = styled.div`
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: #334155;
  margin-bottom: 4px;
`;

const EmptyText = styled.div`
  font-family: NunitoSans; font-size: 12px; color: #64748b; line-height: 1.55;
`;

const EmptyAction = styled.button`
  margin-top: 12px;
  border: none; border-radius: 8px; padding: 8px 14px;
  background: ${ACCENT}; color: #fff; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  &:disabled { opacity: .6; cursor: default; }
`;

const ErrorText = styled.div`
  margin-top: 8px;
  font-family: NunitoSans; font-size: 12px; color: #b91c1c; line-height: 1.5;
`;
